// client/src/context/NotificationContext.js
//
// Global notification system — the single notification API for the entire frontend.
//
// Two ways to use:
//   1. Module-level singleton (works anywhere, inside or outside React):
//        import { notify } from '../context/NotificationContext';
//        notify.success('Saved', 'Profile updated.');
//
//   2. React hook (preferred inside components):
//        import { useNotification } from '../context/NotificationContext';
//        const notify = useNotification();
//        notify.error('Failed', 'Could not load projects.');
//
// Both APIs are identical — same method signatures, same behaviour.
//
// Wraps Chakra UI's useToast internally so the visual appearance is
// identical to existing toasts.  No new dependencies.

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useToast } from '@chakra-ui/react';

// ── Module-level toast reference (set by NotificationProvider) ──────────
let _toastFn = null;

// ── Duplicate-suppression state ─────────────────────────────────────────
const DEDUP_WINDOW_MS = 3000;
const _recentIds = new Map(); // id → timestamp

function _pruneRecentIds(now) {
  for (const [id, ts] of _recentIds) {
    if (now - ts > DEDUP_WINDOW_MS) _recentIds.delete(id);
  }
}

function _shouldSuppress(id, now) {
  if (!id) return false;
  _pruneRecentIds(now);
  if (_recentIds.has(id)) return true;
  _recentIds.set(id, now);
  return false;
}

// ── Default durations (ms) ──────────────────────────────────────────────
const DURATIONS = {
  success: 5000,
  info: 5000,
  warning: 6000,
  error: 7000, // NOT persistent by default — opt-in via { persistent: true }
};

// ── Shared implementation ───────────────────────────────────────────────
function _notify(toastFn, status, title, description, options = {}) {
  if (!toastFn) {
    // Provider not mounted yet — silently drop (avoid crashing during SSR / initial render)
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[notify.${status}] NotificationProvider is not mounted. Toast dropped:`,
        title
      );
    }
    return;
  }

  const now = Date.now();

  // Duplicate suppression (opt-in via options.id)
  if (options.id && _shouldSuppress(options.id, now)) return;

  const duration =
    options.duration !== undefined
      ? options.duration
      : options.persistent
        ? null
        : DURATIONS[status];

  toastFn({
    title,
    description,
    status,
    duration,
    isClosable: options.isClosable !== undefined ? options.isClosable : true,
    position: 'top',
    variant: 'solid',
  });
}

// ── Module-level singleton ──────────────────────────────────────────────
// Usable from utility files, non-React code, and components alike.
export const notify = {
  /**
   * Show a success notification.
   * @param {string} title
   * @param {string} [description]
   * @param {{ duration?: number, persistent?: boolean, isClosable?: boolean, id?: string }} [options]
   */
  success(title, description, options = {}) {
    _notify(_toastFn, 'success', title, description, options);
  },

  /**
   * Show an error notification (auto-dismisses after 7s by default).
   * Pass { persistent: true } to keep it until the user dismisses it.
   * @param {string} title
   * @param {string} [description]
   * @param {{ duration?: number, persistent?: boolean, isClosable?: boolean, id?: string }} [options]
   */
  error(title, description, options = {}) {
    _notify(_toastFn, 'error', title, description, options);
  },

  /**
   * Show a warning notification.
   * @param {string} title
   * @param {string} [description]
   * @param {{ duration?: number, persistent?: boolean, isClosable?: boolean, id?: string }} [options]
   */
  warning(title, description, options = {}) {
    _notify(_toastFn, 'warning', title, description, options);
  },

  /**
   * Show an info notification.
   * @param {string} title
   * @param {string} [description]
   * @param {{ duration?: number, persistent?: boolean, isClosable?: boolean, id?: string }} [options]
   */
  info(title, description, options = {}) {
    _notify(_toastFn, 'info', title, description, options);
  },
};

// ── React context ───────────────────────────────────────────────────────
const NotificationCtx = createContext(null);

/**
 * Mount this ONCE at the app root, INSIDE <ChakraProvider>.
 *
 * <ChakraProvider theme={theme}>
 *   <NotificationProvider>
 *     <App />
 *   </NotificationProvider>
 * </ChakraProvider>
 */
export function NotificationProvider({ children }) {
  const toast = useToast();

  useEffect(() => {
    _toastFn = toast;
    return () => {
      _toastFn = null;
    };
  }, [toast]);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <NotificationCtx.Provider value={value}>
      {children}
    </NotificationCtx.Provider>
  );
}

/**
 * React hook — preferred inside components.
 * Returns the same { success, error, warning, info } API as the module-level `notify`.
 */
export function useNotification() {
  const ctx = useContext(NotificationCtx);

  // Fallback: if provider isn't mounted (shouldn't happen in practice), grab
  // useToast directly so the hook still works.
  const directToast = useToast();
  const toastFn = ctx?.toast || directToast;

  // Use refs for the method identities so the returned object is stable.
  const successRef = useRef(null);
  const errorRef = useRef(null);
  const warningRef = useRef(null);
  const infoRef = useRef(null);

  // Recreate callbacks only when toastFn changes (which is stable from Chakra).
  useEffect(() => {
    successRef.current = (title, description, options) =>
      _notify(toastFn, 'success', title, description, options);
    errorRef.current = (title, description, options) =>
      _notify(toastFn, 'error', title, description, options);
    warningRef.current = (title, description, options) =>
      _notify(toastFn, 'warning', title, description, options);
    infoRef.current = (title, description, options) =>
      _notify(toastFn, 'info', title, description, options);
  });

  return useMemo(
    () => ({
      success: (...args) => successRef.current(...args),
      error: (...args) => errorRef.current(...args),
      warning: (...args) => warningRef.current(...args),
      info: (...args) => infoRef.current(...args),
    }),
    []
  );
}

export default NotificationProvider;
